// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import {
  type AskPunttActionCard,
  type AskPunttActionResolution,
  type AskPunttBriefing,
} from "@mg/schemas/src/christo/askPunttBriefing";
import { Text } from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ActionStack } from "./ActionStack";
import { BriefingHeader } from "./BriefingHeader";
import { DigDeeperSidebar } from "./DigDeeperSidebar";
import { RememberedContext } from "./RememberedContext";
import { visibleActions, waitingBadgeCount } from "./visibleActions";

import { useAppSelector } from "../../utils/hooks";
import {
  ASK_V2_BRIEFING_QUERY_KEY,
  dismissBriefing,
  getBriefing,
  patchBriefingMemory,
  resolveBriefingAction,
} from "../services/briefing";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { useAskV2Transport } from "../transport/useAskV2Transport";

const BRIEFING_POLL_MS = 15_000;

export function AskV2Home() {
  const { t } = useTranslation("ui");
  const queryClient = useQueryClient();
  const userName = useAppSelector((state) => state.auth.value?.name);
  const transport = useAskV2Transport();
  const [digDeeper, setDigDeeper] = useState<AskPunttActionCard | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const briefingQuery = useQuery({
    queryKey: ASK_V2_BRIEFING_QUERY_KEY,
    queryFn: getBriefing,
    refetchInterval: BRIEFING_POLL_MS,
  });

  const resolveMutation = useMutation({
    mutationFn: (vars: {
      card: AskPunttActionCard;
      resolution: AskPunttActionResolution;
    }) => resolveBriefingAction(vars.card.id, vars.resolution),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ASK_V2_BRIEFING_QUERY_KEY });
      const previous = queryClient.getQueryData<AskPunttBriefing>(
        ASK_V2_BRIEFING_QUERY_KEY,
      );
      queryClient.setQueryData<AskPunttBriefing>(
        ASK_V2_BRIEFING_QUERY_KEY,
        (current) => {
          if (!current) return current;
          const remaining = current.actions.filter(
            (card) => card.id !== vars.card.id,
          );
          return {
            ...current,
            actions: remaining,
            guarantee: {
              acted: current.guarantee.acted || vars.resolution === "approved",
            },
            interruptPolicy:
              vars.resolution === "not_now"
                ? "silent"
                : remaining.length === 0
                  ? "silent"
                  : current.interruptPolicy,
          };
        },
      );
      if (vars.resolution === "dig_deeper") {
        setDigDeeper(vars.card);
        setSidebarOpen(false);
      }
      return { previous };
    },
    onError: (_error, vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ASK_V2_BRIEFING_QUERY_KEY, context.previous);
      }
      if (vars.resolution === "dig_deeper") {
        setDigDeeper(null);
        setSidebarOpen(false);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ASK_V2_BRIEFING_QUERY_KEY, data.briefing);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: dismissBriefing,
    onSuccess: (briefing) => {
      queryClient.setQueryData(ASK_V2_BRIEFING_QUERY_KEY, briefing);
      setDigDeeper(null);
      setSidebarOpen(false);
    },
  });

  const memoryMutation = useMutation({
    mutationFn: (vars: { id: string; text: string }) =>
      patchBriefingMemory(vars.id, vars.text),
    onSuccess: (data) => {
      queryClient.setQueryData(ASK_V2_BRIEFING_QUERY_KEY, data.briefing);
    },
  });

  const briefing = briefingQuery.data;
  const actions = visibleActions(briefing);
  const badgeCount = waitingBadgeCount(briefing);

  return (
    <div
      data-testid="ask-v2-home"
      className="flex min-h-0 w-full flex-1 overflow-hidden"
    >
      <div className="flex min-h-0 flex-1 flex-col items-center gap-6 overflow-y-auto px-6 py-8">
        <BriefingHeader
          briefing={briefing}
          userName={userName}
          failed={briefingQuery.isError}
        />
        {badgeCount > 0 ? (
          <Text as="p" size="2" data-testid="ask-v2-badge">
            {t("askV2.home.badgeWaiting", { count: badgeCount })}
          </Text>
        ) : null}
        <RememberedContext
          items={briefing?.rememberedContext ?? []}
          savingId={
            memoryMutation.isPending ? memoryMutation.variables?.id : null
          }
          onCorrect={(id, text) => memoryMutation.mutateAsync({ id, text })}
        />
        <ActionStack
          actions={actions}
          resolvingId={
            resolveMutation.isPending
              ? resolveMutation.variables?.card.id
              : null
          }
          digDeeper={digDeeper}
          onResolve={(card, resolution) => {
            resolveMutation.mutate({ card, resolution });
          }}
          onExpandDigDeeper={() => setSidebarOpen(true)}
        />
        <AskV2Thread transport={transport} />
        <AskV2SummonBar
          transport={transport}
          dismissing={dismissMutation.isPending}
          onDismiss={() => {
            dismissMutation.mutate();
          }}
        />
      </div>
      {sidebarOpen && digDeeper ? (
        <DigDeeperSidebar
          card={digDeeper}
          onClose={() => setSidebarOpen(false)}
        />
      ) : null}
    </div>
  );
}
